<template>
  <Teleport to="body">
    <div class="onboard-overlay" @click.self="$emit('dismiss')">
      <div class="onboard-card">
        <!-- Progress bar -->
        <div class="onboard-progress">
          <div class="onboard-progress-fill" :style="{ width: ((step / totalSteps) * 100) + '%' }" />
        </div>

        <!-- Step content -->
        <transition name="slide" mode="out-in">
          <!-- Step 1: Welcome -->
          <div v-if="step === 1" key="1" class="onboard-step">
            <div class="onboard-icon">🏗️</div>
            <h2>Bem-vindo ao {{ systemName }}!</h2>
            <p>Vamos configurar o seu sistema em poucos passos. Isso leva menos de 2 minutos.</p>
            <div class="onboard-checklist">
              <div class="check-item" :class="{ done: hasSiteConfig }">
                <span class="check-dot" />
                Personalizar nome e visual
              </div>
              <div class="check-item" :class="{ done: hasProject }">
                <span class="check-dot" />
                Criar primeiro projeto
              </div>
              <div class="check-item" :class="{ done: hasUser }">
                <span class="check-dot" />
                Adicionar ou convidar usuários
              </div>
            </div>
          </div>

          <!-- Step 2: Configure system -->
          <div v-else-if="step === 2" key="2" class="onboard-step">
            <div class="onboard-icon">🎨</div>
            <h2>Configure o sistema</h2>
            <p>Acesse <strong>Gerenciar Sistema</strong> na barra lateral para definir o nome da obra, logo e cores personalizadas.</p>
            <div class="onboard-action-card" @click="$emit('go-to', 'manage-system')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.22 3.22l1.42 1.42M15.36 15.36l1.42 1.42M3.22 16.78l1.42-1.42M15.36 4.64l1.42-1.42" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              Ir para Gerenciar Sistema
            </div>
          </div>

          <!-- Step 3: Create project -->
          <div v-else-if="step === 3" key="3" class="onboard-step">
            <div class="onboard-icon">📋</div>
            <h2>Crie seu primeiro projeto</h2>
            <p>Projetos são o centro do sistema. Cada obra tem seu próprio Gantt, equipe, almoxarifado e relatórios.</p>
            <div class="onboard-action-card" @click="$emit('go-to', 'manage-projects')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="3" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 10h6M10 7v6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              Ir para Gerenciar Projetos
            </div>
          </div>

          <!-- Step 4: Invite users -->
          <div v-else-if="step === 4" key="4" class="onboard-step">
            <div class="onboard-icon">👥</div>
            <h2>Adicione sua equipe</h2>
            <p>Convide colaboradores por e-mail ou crie contas diretamente. Defina papéis e cargos para cada pessoa.</p>
            <div class="onboard-action-card" @click="$emit('go-to', 'manage-users')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="8" cy="7" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M2 17c0-3.314 2.686-6 6-6M14 11v6M11 14h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              Ir para Gerenciar Usuários
            </div>
          </div>

          <!-- Step 5: Done -->
          <div v-else-if="step === 5" key="5" class="onboard-step">
            <div class="onboard-icon">✅</div>
            <h2>Tudo pronto!</h2>
            <p>Seu sistema está configurado. Explore o painel, crie tarefas no Gantt e acompanhe o progresso da obra em tempo real.</p>
            <div class="onboard-tips">
              <div class="tip-item">
                <span class="tip-key">Dica</span> Use o Gantt para planejar tarefas com dependências e prazos.
              </div>
              <div class="tip-item">
                <span class="tip-key">Dica</span> Envie RDOs diários pela tela de Relatórios.
              </div>
              <div class="tip-item">
                <span class="tip-key">Dica</span> O Almoxarifado controla ferramentas e equipamentos por tarefa.
              </div>
            </div>
          </div>
        </transition>

        <!-- Navigation -->
        <div class="onboard-nav">
          <button v-if="step > 1" class="onboard-btn-ghost" @click="step--">Voltar</button>
          <span v-else class="onboard-step-label">{{ step }} / {{ totalSteps }}</span>
          <div class="onboard-dots">
            <span v-for="i in totalSteps" :key="i" class="onboard-dot" :class="{ active: i === step }" @click="step = i" />
          </div>
          <button v-if="step < totalSteps" class="onboard-btn-primary" @click="step++">Próximo</button>
          <button v-else class="onboard-btn-primary" @click="$emit('dismiss')">Começar</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

export default defineComponent({
  name: 'OnboardingWizard',
  props: {
    systemName: { type: String, default: 'Obra Tycoon' },
    hasSiteConfig: { type: Boolean, default: false },
    hasProject: { type: Boolean, default: false },
    hasUser: { type: Boolean, default: false }
  },
  emits: ['dismiss', 'go-to'],
  setup() {
    const step = ref(1)
    const totalSteps = 5
    return { step, totalSteps }
  }
})
</script>

<style scoped lang="scss">
.onboard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.onboard-card {
  background: var(--card, #fff);
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,.2);
}

.onboard-progress {
  height: 3px;
  background: var(--border, rgba(0,0,0,.1));
}
.onboard-progress-fill {
  height: 100%;
  background: var(--accent, #2563eb);
  transition: width .4s ease;
}

.onboard-step {
  padding: 36px 36px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 280px;
}

.onboard-icon { font-size: 40px; }
h2 { margin: 0; font-size: 22px; color: var(--ink, #1f1e1a); }
p { margin: 0; font-size: 14px; color: var(--ink-muted, #888); line-height: 1.6; }

.onboard-checklist {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}
.check-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--ink-muted, #888);
  &.done { color: #15803d; }
  &.done .check-dot { background: #22c55e; }
}
.check-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--border, rgba(0,0,0,.2));
  flex-shrink: 0;
}

.onboard-action-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: var(--accent, #2563eb);
  color: #fff;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: opacity .15s;
  &:hover { opacity: .88; }
}

.onboard-tips {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tip-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-muted, #888);
}
.tip-key {
  font-size: 10px;
  font-weight: 700;
  background: var(--accent, #2563eb);
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.onboard-nav {
  padding: 16px 36px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid var(--border, rgba(0,0,0,.08));
}

.onboard-step-label { font-size: 12px; color: var(--ink-muted, #888); }

.onboard-dots {
  display: flex;
  gap: 6px;
}
.onboard-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border, rgba(0,0,0,.15));
  cursor: pointer;
  transition: background .2s;
  &.active { background: var(--accent, #2563eb); }
}

.onboard-btn-primary {
  padding: 9px 20px;
  background: var(--accent, #2563eb);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: .88; }
}

.onboard-btn-ghost {
  padding: 9px 16px;
  background: transparent;
  border: 1.5px solid var(--border, rgba(0,0,0,.15));
  border-radius: 8px;
  font-size: 14px;
  color: var(--ink-muted, #888);
  cursor: pointer;
  &:hover { border-color: var(--ink-muted, #888); }
}

.slide-enter-active, .slide-leave-active { transition: all .25s ease; }
.slide-enter-from { opacity: 0; transform: translateX(30px); }
.slide-leave-to { opacity: 0; transform: translateX(-30px); }
</style>
